import { useMemo, useState } from "react";
import { sortBy } from "lodash";
import { DateTime, StrategyDB, StrategyOutput } from "../../../types";
import { dayForDateTime, formatDateTime, formatDay, getDateTime } from "../../services/date";
import { Percent, USD } from ".";

type RowDefinition = {
    header?: string,
    type?: "date" | "number" | "string" | "percent", // default string
    width?: number | string,
    render?: (value: any) => React.ReactNode,
}

type Sort<T> = { key: keyof T, asc: boolean };

type TableProps<T> = {
    data: T[],
    rows: Partial<Record<keyof T, RowDefinition>>,
    pageSite?: number,
    defaultSort?: null | Sort<T>,
    onClick?: (index: number) => void,
}

export default function Table<T>({ data, rows, pageSite = 20, defaultSort = null, onClick }: TableProps<T>) {
    if (data.length === 0) return <p>Žádná data</p>;

    const [page, setPage] = useState(0);
    const [sort, setSort] = useState<null | Sort<T>>(defaultSort);

    const tableData = useMemo(() => {
        let tableData = data.map((row, i) => ({ ...row, index: i }));
        if (sort) {
            tableData = sortBy(tableData, sort.key);
            if (!sort.asc) tableData = tableData.reverse();
        }

        return tableData
            .slice(page * pageSite, (page + 1) * pageSite);
    }, [data, pageSite, page, sort]);

    return <div className="block">
        <div className="table-wrapper">
            <table className={onClick ? "table-clickable" : "table"}>
                <thead>
                    <tr>
                        {Object.entries(rows).map(([key, def]) => <th key={key} onClick={() => setSort({ key: key as keyof T, asc: (sort && sort.key === key) ? !sort.asc : true })}>
                            {(def as RowDefinition).header ?? key}
                            {sort && sort.key === key && (sort.asc ? "↓" : "↑")}
                        </th>)}
                    </tr>
                </thead>
                <tbody>
                    {tableData.map((row, i) => <tr onClick={() => onClick?.(row.index)} key={i}>
                        {Object.entries(rows).map(([key, def]) => {
                            const value = row[key as keyof T];
                            const { type, width, render } = def as RowDefinition;

                            if (render) {
                                return <td key={key} style={{ width }}>{render(value)}</td>;
                            }

                            if (type === "date") {
                                return <td key={key} style={{ width }}>{formatDateTime(Number(value))}</td>;

                            } else if (type === "number") {
                                return <td key={key} style={{ width }}>{Number(value).toLocaleString()}</td>;
                            } else if (type === "percent") {
                                return <td key={key} style={{ width }}>{(Number(value) * 100).toFixed(2) + " %"}</td>;
                            }

                            if (typeof value === "number")
                                return <td key={key} style={{ width }}>{value.toLocaleString()}</td>;

                            if (typeof value === "string")
                                return <td key={key} style={{ width }}>{value}</td>;

                            return <td key={key} style={{ width }}>{JSON.stringify(value, null, 2)}</td>;
                        })}
                    </tr>)}
                </tbody>
            </table>
            <div className="pagination">
                Řádků: {data.length} | Stránka: <button onClick={() => setPage(page - 1)} disabled={page === 0}>{page - 1}</button> {page + 1} / {Math.ceil(data.length / pageSite)} <button onClick={() => setPage(page + 1)} disabled={page === Math.ceil(data.length / pageSite) - 1}>{page + 2}</button>
            </div>
        </div>
    </div>;
}


export function TableStrategy({ data, onClick }: { data: StrategyDB[], onClick?: (row: StrategyDB) => void }) {

    const dataTable = useMemo(() => {
        return data.map(({ stats, ...rest }) => {
            // const { allNumber, tradesNumber, winRate, } = stats;
            return { ...rest, ...stats };
        })
    }, [data]);

    return <Table data={dataTable} pageSite={5} defaultSort={{ key: "date", asc: false }} onClick={index => onClick?.(data[index])} rows={{
        date: { type: "date" },
        strategy: {},
        // allNumber: {},
        dataset: {},
        tradesNumber: {},
        gainPerYear: { type: "percent" },
        gainPerTradeAverage: { type: "percent" },
        winRate: { type: "percent" },
        profitFactor: {},
        drawdown: { type: "percent" },
        riskToRewardMedian: { type: "percent" },
        durationMedian: { render: (v) => `${(Number(v) / dayForDateTime).toFixed(1)} dnů` },
        stopLossNumber: {},
        takeProfitNumber: {},
    }} />;
}

export function TableStrategyOutput({ data, onClick }: { data: StrategyOutput[], onClick?: (row: StrategyOutput) => void }) {

    const tableData = useMemo(() => {
        return data.map(({ symbol, date, decision, result }, index) => {

            if (result.typeClose !== "not-open") {
                return {
                    index,
                    symbol,
                    typeClose: result.typeClose,
                    date,
                    gain: 1 - result.open / result.close,
                    open: result.open,
                    close: result.close,
                    takeProfit: decision.takeProfit,
                    stopLoss: decision.stopLoss,
                    price: decision.price,
                };
            } else {
                return {
                    index,
                    symbol,
                    typeClose: result.typeClose,
                    date,
                    gain: 0,
                    open: 0,
                    close: 0,
                    takeProfit: decision.takeProfit,
                    stopLoss: decision.stopLoss,
                    price: decision.price,
                };
            }
        });
    }, [data]);

    return <Table
        data={tableData}
        onClick={index => onClick?.(data[tableData[index].index])}
        defaultSort={{ key: "gain", asc: false }}
        rows={{
            symbol: {},
            date: { type: "date" },
            typeClose: {},
            gain: { type: "percent" },
            open: {},
            close: {},
            takeProfit: {},
            stopLoss: {},
            price: {},
        }}
    />
}

/*
function Cell({ value }: { value: any }) {
    if (typeof value === "string")
        return value;

    if (typeof value === "number") {
        if (isDateTime(value))
            return formatDateTime(value);

        if (value > -2 && value < 2)
            return (value * 100).toFixed(2) + " %";

        return (value as number).toLocaleString();
    }

    return JSON.stringify(value, null, 2);
}
*/

type TableColumn<T> =
    | { title: string, key: keyof T, render?: "string" | "boolean" | "number" | "percent" | "usd" | "json" | "date" | "datetime" }
    | { title: string, render: (row: T) => React.ReactNode };

type TableInlineProps<T> = {
    rows: T[],
    columns: (null | TableColumn<T>)[],
}


// Optimize for mail
export function TableInline<T>({ rows, columns: cs }: TableInlineProps<T>) {
    const columns = cs.filter(c => c !== null) as TableColumn<T>[];

    return <>
        {/* <p>Počet řádků: {rows.length}</p> */}
        {rows.length > 0 && <div className="table-wrapper">
            <table>
                <thead>
                    <tr>
                        {columns.map((c, i) => <th key={i} style={{ textAlign: getAlign(c) }}>{c.title}</th>)}
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row, i) => <tr key={i}>
                        {columns.map((c, i) => {
                            if (typeof c.render === "function") {
                                return <td key={i} style={{ textAlign: getAlign(c) }}>{c.render(row)}</td>
                            }

                            let value: unknown = row[c.key];

                            let type = c.render;
                            if (!type) {
                                const valueType = typeof value;
                                switch (valueType) {
                                    case "string":
                                        type = "string";
                                        break;
                                    case "boolean":
                                        type = "boolean";
                                        break;
                                    case "number":
                                    case "bigint":
                                        type = "number";
                                        break;
                                    default:
                                        type = "json";
                                        break;
                                }
                            }

                            if ((type === "date" || type === "datetime") && typeof value === "string")
                                value = getDateTime(value);

                            return <td key={i} style={{ textAlign: getAlign(c) }}>
                                {type === "string" && String(value)}
                                {type === "boolean" && (value ? "✅" : "❌")}
                                {type === "number" && Number(value).toLocaleString()}
                                {type === "percent" && <Percent value={Number(value)} />}
                                {type === "usd" && <USD value={Number(value || 0)} />}
                                {type === "date" && formatDay(value as DateTime)}
                                {type === "datetime" && formatDateTime(value as DateTime)}
                                {type === "json" && value !== null && <div
                                    title={JSON.stringify(value, null, 4)}
                                    style={{
                                        display: "-webkit-box",
                                        maxWidth: "200px",
                                        margin: "0px auto",
                                        fontSize: "50%",
                                        overflow: "hidden",
                                        lineClamp: 5,
                                        WebkitLineClamp: 5,
                                        WebkitBoxOrient: "vertical",
                                    }}>{JSON.stringify(value)}</div>}
                            </td>;
                        })}
                    </tr>)}
                </tbody>
            </table>
        </div>}
    </>;
}


// Helper function for action badge rendering in email tables
function renderCellStringValue(val: string) {
    const s = String(val);
    if (s === "Buy" || s === "Buy (regression)") {
        return <span style={{ display: "inline-block", backgroundColor: "#dcfce7", color: "#15803d", border: "1px solid #bbf7d0", borderRadius: "4px", padding: "2px 8px", fontSize: "11px", fontWeight: "bold" }}>🟢 {s}</span>;
    }
    if (s === "Sell") {
        return <span style={{ display: "inline-block", backgroundColor: "#fee2e2", color: "#b91c1c", border: "1px solid #fecaca", borderRadius: "4px", padding: "2px 8px", fontSize: "11px", fontWeight: "bold" }}>🔴 {s}</span>;
    }
    if (s.includes("25%") || s.includes("Too big")) {
        return <span style={{ display: "inline-block", backgroundColor: "#fef3c7", color: "#b45309", border: "1px solid #fde68a", borderRadius: "4px", padding: "2px 8px", fontSize: "11px", fontWeight: "bold" }}>⚠️ {s}</span>;
    }
    if (s === "none") {
        return <span style={{ color: "#94a3b8", fontSize: "12px" }}>—</span>;
    }
    return s;
}

// Optimize for mail
export function TableMail<T>({ rows, columns: cs }: TableInlineProps<T>) {
    const columns = cs.filter(c => c !== null) as TableColumn<T>[];

    if (rows.length === 0) {
        return <div style={{ padding: "12px", textAlign: "center", backgroundColor: "#f8fafc", borderRadius: "6px", border: "1px solid #e2e8f0", color: "#64748b", fontSize: "13px", margin: "10px 0" }}>Žádné položky</div>;
    }

    return <div style={{ margin: "14px 0 24px 0" }}>
        <div style={{ fontSize: "12px", color: "#64748b", marginBottom: "8px", fontWeight: 600 }}>Počet položek: {rows.length}</div>
        <div style={{ width: "100%", overflowX: "auto", borderRadius: "8px", border: "1px solid #e2e8f0", backgroundColor: "#ffffff" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: "Arial, sans-serif", fontSize: "13px" }}>
                <thead>
                    <tr style={{ backgroundColor: "#f1f5f9" }}>
                        {columns.map((c, i) => (
                            <th key={i} style={{ 
                                textAlign: getAlign(c), 
                                padding: "10px 12px", 
                                color: "#475569", 
                                fontSize: "11px", 
                                fontWeight: "bold", 
                                textTransform: "uppercase", 
                                letterSpacing: "0.5px", 
                                borderBottom: "2px solid #e2e8f0",
                                whiteSpace: "nowrap" 
                            }}>
                                {c.title}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row, rowIndex) => (
                        <tr key={rowIndex} style={{ backgroundColor: rowIndex % 2 === 1 ? "#f8fafc" : "#ffffff", borderBottom: rowIndex < rows.length - 1 ? "1px solid #f1f5f9" : "none" }}>
                            {columns.map((c, colIndex) => {
                                if (typeof c.render === "function") {
                                    return <td key={colIndex} style={{ textAlign: getAlign(c), padding: "9px 12px", color: "#1e293b", verticalAlign: "middle" }}>{c.render(row)}</td>;
                                }

                                let value: unknown = row[c.key];

                                let type = c.render;
                                if (!type) {
                                    const valueType = typeof value;
                                    switch (valueType) {
                                        case "string":
                                            type = "string";
                                            break;
                                        case "boolean":
                                            type = "boolean";
                                            break;
                                        case "number":
                                        case "bigint":
                                            type = "number";
                                            break;
                                        default:
                                            type = "json";
                                            break;
                                    }
                                }

                                if ((type === "date" || type === "datetime") && typeof value === "string")
                                    value = getDateTime(value);

                                return <td key={colIndex} style={{ textAlign: getAlign(c), padding: "9px 12px", color: "#1e293b", verticalAlign: "middle" }}>
                                    {type === "string" && renderCellStringValue(String(value))}
                                    {type === "boolean" && (value ? <span style={{ color: "#16a34a", fontWeight: "bold" }}>Ano</span> : <span style={{ color: "#dc2626" }}>Ne</span>)}
                                    {type === "number" && Number(value).toLocaleString()}
                                    {type === "percent" && <Percent value={Number(value)} />}
                                    {type === "usd" && <USD value={Number(value || 0)} />}
                                    {type === "date" && formatDay(value as DateTime)}
                                    {type === "datetime" && formatDateTime(value as DateTime)}
                                    {type === "json" && value !== null && <div
                                        title={JSON.stringify(value, null, 4)}
                                        style={{
                                            display: "-webkit-box",
                                            maxWidth: "200px",
                                            margin: "0 auto",
                                            fontSize: "11px",
                                            color: "#e11d48",
                                            backgroundColor: "#fff1f2",
                                            padding: "2px 6px",
                                            borderRadius: "4px",
                                            overflow: "hidden",
                                            lineClamp: 3,
                                            WebkitLineClamp: 3,
                                            WebkitBoxOrient: "vertical",
                                        }}>{JSON.stringify(value)}</div>}
                                </td>;
                            })}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    </div>;
}


function getAlign<T>({ render }: TableColumn<T>): "center" | "left" | "right" {
    switch (render) {
        case "date":
        case "datetime":
            return "left";
        case "number":
        case "percent":
        case "usd":
            return "right";
        default:
            return "center";

    }
}
